import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * VAT format patterns for EU countries
 * Source: https://ec.europa.eu/taxation_customs/vies/faq.html
 */
const VAT_FORMAT_PATTERNS: Record<string, RegExp> = {
  AT: /^ATU\d{8}$/,
  BE: /^BE[01]\d{9}$/,
  BG: /^BG\d{9,10}$/,
  CY: /^CY\d{8}[A-Z]$/,
  CZ: /^CZ\d{8,10}$/,
  DE: /^DE\d{9}$/,
  DK: /^DK\d{8}$/,
  EE: /^EE\d{9}$/,
  EL: /^EL\d{9}$/, // Greece
  ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
  FI: /^FI\d{8}$/,
  FR: /^FR[A-Z0-9]{2}\d{9}$/,
  HR: /^HR\d{11}$/,
  HU: /^HU\d{8}$/,
  IE: /^IE\d{7}[A-Z]{1,2}$|^IE\d[A-Z]\d{5}[A-Z]$/,
  IT: /^IT\d{11}$/,
  LT: /^LT(\d{9}|\d{12})$/,
  LU: /^LU\d{8}$/,
  LV: /^LV\d{11}$/,
  MT: /^MT\d{8}$/,
  NL: /^NL\d{9}B\d{2}$/,
  PL: /^PL\d{10}$/,
  PT: /^PT\d{9}$/,
  RO: /^RO\d{2,10}$/,
  SE: /^SE\d{12}$/,
  SI: /^SI\d{8}$/,
  SK: /^SK\d{10}$/,
  GB: /^GB(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/, // UK (post-Brexit, may vary)
};

/**
 * VAT format validator - returns warning (not blocking)
 *
 * Usage: Add to form control but allow form submission even with errors
 * Display as warning in template, not as blocking error
 */
export function vatFormatValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || value.length < 4) return null; // Empty or too short = OK

    const normalized = value.toUpperCase().replace(/\s/g, '');
    const countryCode = normalized.substring(0, 2);
    const pattern = VAT_FORMAT_PATTERNS[countryCode];

    if (!pattern) {
      // Country code not recognized
      return {
        vatFormat: {
          message: `Code pays "${countryCode}" non reconnu`,
          isWarning: true
        }
      };
    }

    if (!pattern.test(normalized)) {
      return {
        vatFormat: {
          message: `Format TVA invalide pour ${countryCode}`,
          isWarning: true
        }
      };
    }

    return null;
  };
}

/**
 * Check if VAT format is valid (pure function for use outside validators)
 */
export function isValidVatFormat(vat: string | null | undefined): boolean {
  if (!vat || vat.length < 4) return true; // Empty = valid (optional field)

  const normalized = vat.toUpperCase().replace(/\s/g, '');
  const countryCode = normalized.substring(0, 2);
  const pattern = VAT_FORMAT_PATTERNS[countryCode];

  return pattern ? pattern.test(normalized) : false;
}
